import React from 'react';
import { stub as $t } from '@nbcnews/analytics-framework';
import Link from 'components/Link';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import classNames from 'classnames';
import { Picture } from 'components/Picture';
import { Unibrow } from 'components/Unibrow';
import { Save } from 'components/Social/SocialShareMenu/Save';
import styles from './styles.module.scss';

/**
 * @param {*} props - the properties object
 * @param {object} props.article - the article object containing details like url, teaseImage, unibrow, and headline
 * @param {string} props.fallbackImg - the fallback image URL to use if the article does not have a teaseImage
 * @param {boolean} props.hasImages - flag indicating whether images should be displayed
 * @param {string} props.vertical - the vertical category of the article, used for styling the Unibrow component
 * @param {string} props.icid - tracking identifier for analytics
 * @param {string} props.presentationStyle - presentation style of curated list
 * @param {number} props.position - position of the article in the carousel for tracking purposes
 * @param {boolean} [props.isSingleCard] - flag indicating if the carousel contains a single card
 * @returns {JSX.Element} The CarouselCard component
 */
const CarouselCard = (props) => {
  const {
    article,
    fallbackImg,
    hasImages,
    vertical,
    icid,
    presentationStyle,
    position,
    isSingleCard,
  } = props;
  const {
    url,
    teaseImage,
    unibrow,
    headline,
    id,
  } = article;
  const carouselImg = teaseImage?.url?.primary || fallbackImg;

  /**
   * Handles the click event for a carousel item and tracks the interaction.
   */
  const carouselItemClickHandler = () => {
    $t('track', 'mbt_bacon_click', {
      action: 'itemClick',
      presentation: presentationStyle,
      itemPosition: position,
      itemName: headline?.primary,
      itemId: id,
    });
  };

  return (
    <div className={classNames(styles.carouselCard, isSingleCard && styles.singleCarouselCard)} data-testid="carousel-card">
      {hasImages && (
        <div className={styles.carouselCardImgContainer} data-testid="carousel-image">
          <Link
            href={url?.primary}
            data-icid={icid}
            data-testid="carousel-image-link"
            onClick={carouselItemClickHandler}
          >
            <Picture
              alt={teaseImage?.altText}
              responsiveFlavors={{
                s: AIMS_FLAVORS.FOCAL_600x300,
                m: AIMS_FLAVORS.FOCAL_720x360,
                l: AIMS_FLAVORS.FOCAL_920x460,
                xl: AIMS_FLAVORS.FOCAL_860x430,
              }}
              url={carouselImg}
              originalHeight={teaseImage?.height}
              originalWidth={teaseImage?.width}
              className={styles.pictureContainer}
            />
          </Link>
          <Save
            contentId={id}
            contentType="article"
            options={{ isThumbnail: true, showCTA: true }}
          />
        </div>
      )}
      <div
        className={classNames(
          styles.carouselCardTextContentWrapper,
          !hasImages && styles.carouselCardTextContentWithSave,
        )}
      >
        <div className={styles.carouselCardTextContent}>
          {unibrow && (
            <Unibrow
              unibrow={unibrow}
              hasDefaultTextStyle
              vertical={vertical}
              className={styles.carouselCardUnibrow}
            />
          )}
          {headline?.primary && (
            <Link
              href={url?.primary}
              className={classNames(styles.carouselCardHeadline, hasImages
                ? styles.headlineWithImg
                : styles.headlineNoImg)}
              data-icid={icid}
              data-testid="carousel-headline-link"
              onClick={carouselItemClickHandler}
            >
              {headline?.primary}
            </Link>
          )}
        </div>
        {!hasImages && (
          <Save
            contentId={id}
            contentType="article"
            options={{ isTitle: true }}
          />
        )}
      </div>
    </div>
  );
};

export { CarouselCard };
